import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Redirect } from 'react-router-dom'

import { Box, Table, TableBody, TableContainer, Typography } from '@material-ui/core'

import { SyncLogRow } from './SyncLogRow'
import { ScreenLoader } from './ScreenLoader'
import { SyncLogsTableHead } from './SyncLogsTableHead'

import { useApi } from '../hooks/storeHook'
import { useAccountParamsOptional, useStoreAccount } from '../hooks/routeHooks'
import { useBoolState, useLazyEffect, useUnmountSignal } from '../hooks/commonHooks'

import { appRoutes } from '../common/appRoutes'
import { UnprocessedValueError } from '../common/tsUtils'

import { integrationTypeNames } from '../types/integrationTypeUtils'

import { SyncLogModel } from '../server/mpsklad_core/Models/SyncLogModel'
import { IntegrationType } from '../server/mpsklad_core/Entity/IntegrationType'
import { AccountModelBase } from '../server/mpsklad_core/Models/AccountModelBase'

export type SyncLogsScreenBaseProps = {
  account: AccountModelBase
}

export const SyncLogsScreen =
  observer(() => {
    const {integration, accountId} = useAccountParamsOptional()

    if (!integration || !accountId) {
      return <Box margin={5} height={'calc(100vh - 270px)'}>Выбранного аккаунта не существует.</Box>
    }

    switch (integration) {
      case undefined:
        return null

      case IntegrationType.Ozon:
      case IntegrationType.Wildberries:
      case IntegrationType.YandexMarket:
        return <StoreSyncLogsScreen/>

      case IntegrationType.MoySklad:
        return <Redirect to={appRoutes.MoySklad.syncLogs}/>

      default:
        throw new UnprocessedValueError('IntegrationType', integration)
    }
  })

export const StoreSyncLogsScreen =
  observer(() => {
    const account = useStoreAccount()

    return <SyncLogsScreenBase account={account}/>
  })

export const SyncLogsScreenBase =
  observer(
    ({account}: SyncLogsScreenBaseProps) => {
      const api = useApi()

      const [isLoading, /*setLoading*/, setLoaded] = useBoolState(true)
      const [syncErrors, setSyncErrors] = useState<SyncLogModel[]>([])

      const unmountSignal = useUnmountSignal()

      useLazyEffect(
        async () => {
          try {
            setSyncErrors(
              await api.userSync.syncLogs({
                  integration: account.integrationType,
                  accountId: account.id
                },
                {signal: unmountSignal}))
          } finally {
            setLoaded()
          }
        })

      if (isLoading) {
        return <ScreenLoader/>
      }

      if (syncErrors.length === 0) {
        return (
          <div className="body_wrapper_zero">
            <div className="body_wrapper__two">
              Нет логов синхронизации {integrationTypeNames[account.integrationType]}
            </div>
          </div>
        )
      }

      // TODO: Pagination and stuff
      return (
        <div className="body_wrapper_zero">
          <div className="body_wrapper">
            <Box display="flex" alignItems="center" marginBottom={2}>
              <Typography style={{color: '#1F364D', fontSize: 18, fontWeight: 'bold', textTransform: 'uppercase'}}>
                Логи синхронизации {integrationTypeNames[account.integrationType]}
              </Typography>
            </Box>
          </div>

          <div
            style={{borderRadius: '10px', width: 'calc(100vw - 400px)'}}>
            <TableContainer>
              <Table>
                <SyncLogsTableHead/>

                <TableBody>
                  {
                    syncErrors.map(error => <SyncLogRow key={error.id} log={error}/>)
                  }
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      )
    })